import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import useMediaQuery from '../hooks/useMediaQuery';
import '../styles/searchLectures.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTime(t) {
  if (!t) return '??:??';
  const parts = t.split(':');
  if (parts.length === 3) {
    const h = parseInt(parts[0]);
    const m = parseInt(parts[1]);
    const s = parseInt(parseFloat(parts[2]));
    if (h === 0) return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
    return h + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
  }
  return t;
}

function cleanContent(text) {
  let cleaned = text.replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]+/g, '');
  cleaned = cleaned.replace(/Transliteration:\s*/gi, '');
  cleaned = cleaned.replace(/Translation:\s*/gi, '');
  cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
  cleaned = cleaned.replace(/^\s*[.,;:]\s*/, '').replace(/\s*[.,;:]\s*$/, '');
  return cleaned;
}

function excerptContent(text, maxLen = 180) {
  if (text.length <= maxLen) return { excerpt: text, full: null };
  let cut = text.lastIndexOf('. ', maxLen);
  if (cut < maxLen * 0.5) cut = text.lastIndexOf(', ', maxLen);
  if (cut < maxLen * 0.5) cut = maxLen;
  return { excerpt: text.substring(0, cut + 1).trim(), full: text };
}

function simClass(sim) {
  if (sim >= 0.70) return 'search-sim-strong';
  if (sim >= 0.60) return 'search-sim-good';
  return 'search-sim-fair';
}

const PLAY_SVG = (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
    <path d="M21.582 11.13L4.394 1.266C3.629.823 2.862 1.372 2.862 2.268v19.464c0 .896.767 1.445 1.532.998l17.188-9.864c.77-.448.77-1.288 0-1.736z" />
  </svg>
);

const EXAMPLES = [
  'Imam Hussain and Karbala',
  'How to prepare for Ramadan',
  'Patience in times of difficulty',
  'Dua and supplication',
  'Signs of the reappearance of Imam Mahdi',
  'Importance of prayer and salah',
  'Fasting and spiritual purification',
  'Justice and fighting oppression',
];

const TOP_RESULTS_VISIBLE = 5;

// ─── Component ──────────────────────────────────────────────────────────────

// API URL: works with both netlify dev (proxy) and react-scripts start (direct)
const SEARCH_API_URL = '/.netlify/functions/search';

const SearchLecturesPage = () => {
  const { config, theme, loading: configLoading, error: configError } = useConfig();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const isMobileView = useMediaQuery('(max-width: 768px)');

  // State
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ speakers: [], events: [] });
  const [stats, setStats] = useState({ lectures: 0, chunks: 0 });
  const [activeFilters, setActiveFilters] = useState({ speaker: null, event: null });
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activePreviewId, setActivePreviewId] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [hasSearched, setHasSearched] = useState(false);

  const dropdownRef = useRef(null);
  const primaryColor = theme?.colors?.primary || '#1e4273';
  const headingColor = theme?.colors?.headingColor || primaryColor;

  // Load filters on mount
  useEffect(() => {
    if (!config?.google_sheet_id) return;

    const loadFilters = async () => {
      try {
        const resp = await fetch(SEARCH_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sheet_id: config.google_sheet_id, action: 'filters' }),
        });
        const data = await resp.json();
        setFilters({ speakers: data.speakers || [], events: data.events || [] });
        setStats(data.stats || { lectures: 0, chunks: 0 });
      } catch (e) {
        console.error('Failed to load filters:', e);
      } finally {
        setFiltersLoading(false);
      }
    };

    loadFilters();
  }, [config]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Search function
  const doSearch = useCallback(async (searchQuery) => {
    const q = (searchQuery || query).trim();
    if (!q || !config?.google_sheet_id) return;

    setLoading(true);
    setHasSearched(true);
    setShowAll(false);
    setActivePreviewId(null);
    setExpandedCards(new Set());

    try {
      const resp = await fetch(SEARCH_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: q,
          sheet_id: config.google_sheet_id,
          speaker: activeFilters.speaker,
          event: activeFilters.event,
          top_k: 15,
        }),
      });
      const data = await resp.json();
      setResults(data.results || []);
      setMeta(data.meta || {});
      if (data.filters) {
        setFilters({ speakers: data.filters.speakers || [], events: data.filters.events || [] });
      }
    } catch (e) {
      console.error('Search error:', e);
      setResults([]);
      setMeta({ error: e.message });
    } finally {
      setLoading(false);
    }
  }, [query, config, activeFilters]);

  // Auto-re-search on filter change
  const handleFilterChange = useCallback((newFilters) => {
    setActiveFilters(newFilters);
    if (query.trim()) {
      // Trigger search with new filters on next tick
      setTimeout(() => doSearch(query), 0);
    }
  }, [query, doSearch]);

  const selectSpeaker = (name) => {
    setDropdownOpen(false);
    handleFilterChange({ ...activeFilters, speaker: name });
  };

  const selectEvent = (name) => {
    const newEvent = activeFilters.event === name ? null : name;
    handleFilterChange({ ...activeFilters, event: newEvent });
  };

  const clearAllFilters = () => {
    handleFilterChange({ speaker: null, event: null });
  };

  const togglePreview = (cardId, ytId, startSec) => {
    if (activePreviewId === cardId) {
      setActivePreviewId(null);
    } else {
      setActivePreviewId(cardId);
    }
  };

  const toggleExpand = (cardId) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') doSearch();
  };

  const exampleSearch = (text) => {
    setQuery(text);
    setTimeout(() => doSearch(text), 0);
  };

  // Visible results
  const visibleResults = showAll ? results : results.slice(0, TOP_RESULTS_VISIBLE);
  const hiddenCount = results.length - TOP_RESULTS_VISIBLE;

  // ─── Render ─────────────────────────────────────────────────────────────

  const Layout = isMobileView ? MobileLayout : DesktopLayout;
  const bgColor = theme?.colors?.background || '#f8f9fa';

  // Config loading guard (matching NewHomePage pattern)
  if (configLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: primaryColor }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 16 }}>Loading...</div>
            <div style={{ width: 40, height: 40, margin: '0 auto', border: '4px solid #e0e0e0', borderTopColor: primaryColor, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          </div>
        </div>
      </Layout>
    );
  }

  if (configError) {
    return (
      <Layout>
        <div style={{ padding: 40, textAlign: 'center', color: '#e74c3c' }}>
          <h2>Error Loading Configuration</h2>
          <p>{configError.message || String(configError)}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="search-page" style={{ backgroundColor: bgColor, minHeight: 'calc(100vh - 160px)' }}>
      {/* Title */}
      <h1 className="search-page-title" style={{ color: headingColor }}>Search Lectures</h1>
      <p className="search-page-subtitle">
        {filtersLoading
          ? 'Preparing search...'
          : stats.lectures > 0
            ? `Search across ${stats.lectures} lectures · ${stats.chunks} indexed moments`
            : 'Search across lecture transcriptions'}
      </p>

      {/* Indexing indicator — shown while the server builds the search index */}
      {filtersLoading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '14px 18px',
          marginBottom: '16px',
          borderRadius: '12px',
          background: `${primaryColor}08`,
          border: `1px solid ${primaryColor}20`,
        }}>
          <div style={{
            width: '20px', height: '20px', flexShrink: 0,
            border: `2px solid ${primaryColor}30`,
            borderTop: `2px solid ${primaryColor}`,
            borderRadius: '50%',
            animation: 'indexSpin 1s linear infinite',
          }} />
          <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
            <strong style={{ color: primaryColor }}>Indexing lectures...</strong> This takes a few seconds on first visit. You'll be able to search once it's ready.
          </div>
          <style>{`@keyframes indexSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Search Input */}
      <div className="search-input-row">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='Ask anything... e.g. "What does Islam say about patience?"'
          autoFocus
          style={{ '--theme-primary': primaryColor }}
        />
        <button
          onClick={() => doSearch()}
          disabled={loading || filtersLoading || !query.trim()}
          style={{ background: primaryColor }}
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Filter Row */}
      <div className="search-filter-row">
        <div className="search-filter-dropdown" ref={dropdownRef}>
          <button
            className={`search-filter-trigger ${dropdownOpen ? 'active' : ''}`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{ '--theme-primary': primaryColor }}
          >
            {activeFilters.speaker || 'All Speakers'} <span style={{ fontSize: 10 }}>▾</span>
          </button>
          <div className={`search-filter-panel ${dropdownOpen ? 'open' : ''}`}>
            <div
              className={`search-filter-option ${!activeFilters.speaker ? 'selected' : ''}`}
              onClick={() => selectSpeaker(null)}
              style={{ '--theme-primary': primaryColor }}
            >
              All Speakers
              <span className="count">{filters.speakers.reduce((s, x) => s + x.lectures, 0)}</span>
            </div>
            {filters.speakers.map((s) => (
              <div
                key={s.name}
                className={`search-filter-option ${activeFilters.speaker === s.name ? 'selected' : ''}`}
                onClick={() => selectSpeaker(s.name)}
                style={{ '--theme-primary': primaryColor }}
              >
                {s.name}
                <span className="count">{s.lectures} lectures</span>
              </div>
            ))}
          </div>
        </div>

        <div className="search-series-chips">
          {filters.events.map((e) => (
            <button
              key={e.name}
              className={`search-series-chip ${activeFilters.event === e.name ? 'active' : ''}`}
              onClick={() => selectEvent(e.name)}
              title={e.name}
              style={{ '--theme-primary': primaryColor }}
            >
              {e.name.length > 35 ? e.name.substring(0, 32) + '...' : e.name}
            </button>
          ))}
        </div>
      </div>

      {/* Active Filter Pills */}
      {(activeFilters.speaker || activeFilters.event) && (
        <div className="search-active-filters">
          {activeFilters.speaker && (
            <span className="search-filter-pill" style={{ '--theme-primary': primaryColor }}>
              {activeFilters.speaker}
              <span className="remove" onClick={() => selectSpeaker(null)}>×</span>
            </span>
          )}
          {activeFilters.event && (
            <span className="search-filter-pill" style={{ '--theme-primary': primaryColor }}>
              {activeFilters.event.length > 40 ? activeFilters.event.substring(0, 37) + '...' : activeFilters.event}
              <span className="remove" onClick={() => selectEvent(null)}>×</span>
            </span>
          )}
          <button className="search-clear-filters" onClick={clearAllFilters}>Clear all</button>
        </div>
      )}

      {/* Stats */}
      {meta && (
        <div className="search-stats">
          <span>
            <span className="highlight" style={{ color: primaryColor }}>{meta.results_found}</span> results
            {meta.speaker_filter && (
              <span style={{ color: primaryColor }}> · {meta.speaker_filter}</span>
            )}
          </span>
          <span>{meta.search_time_ms}ms</span>
        </div>
      )}

      {/* Example Buttons (shown when no search yet) */}
      {!hasSearched && !loading && (
        <div className="search-examples">
          {EXAMPLES.map((text) => (
            <button key={text} onClick={() => exampleSearch(text)}>{text}</button>
          ))}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="search-loading">
          <div className="search-spinner" style={{ borderTopColor: primaryColor }} />
          <br />Searching...
        </div>
      )}

      {/* Empty State */}
      {hasSearched && !loading && results.length === 0 && (
        <div className="search-empty">
          <div className="icon">🔍</div>
          No results found. Try a broader or different query.
        </div>
      )}

      {/* Results */}
      {!loading && visibleResults.map((item, idx) => {
        const cardId = `card-${idx}`;
        const pct = (item.similarity * 100).toFixed(0);
        const startSec = Math.floor(item.start_seconds);
        const ytId = item.youtube_id || '';
        const initials = item.speaker.split(' ').filter(w => w).map(w => w[0]).join('').substring(0, 2).toUpperCase();
        const cleaned = cleanContent(item.content);
        const { excerpt, full } = excerptContent(cleaned);
        const isExpanded = expandedCards.has(cardId);
        const isPreviewOpen = activePreviewId === cardId;

        return (
          <div key={cardId} className="search-result-card">
            <div className="search-result-main">
              {/* Top row: speaker + timestamp + watch + badge */}
              <div className="search-result-top">
                <div className="search-result-top-left">
                  <span className="search-speaker-tag">
                    <span className="search-speaker-icon" style={{ background: `rgba(${theme?.colors?.primary ? '30,66,115' : '30,66,115'}, 0.08)`, color: primaryColor }}>{initials}</span>
                    {item.speaker}
                  </span>
                  <span className="search-timestamp" style={{ color: primaryColor, background: `rgba(30,66,115,0.06)` }}>
                    ⏱ {formatTime(item.start_time)} → {formatTime(item.end_time)}
                  </span>
                  {ytId && (
                    <button
                      className={`search-watch-btn ${isPreviewOpen ? 'active' : ''}`}
                      onClick={() => togglePreview(cardId, ytId, startSec)}
                    >
                      {PLAY_SVG} {isPreviewOpen ? 'Playing...' : 'Watch'}
                    </button>
                  )}
                  <button
                    className="search-open-lecture-btn"
                    onClick={() => navigate(`/lecture/${item.lecture_id}?tab=watch&t=${startSec}`)}
                    style={{ color: primaryColor, borderColor: `rgba(30,66,115,0.2)`, background: `rgba(30,66,115,0.06)` }}
                  >
                    Open Lecture ↗
                  </button>
                </div>
                <span className={`search-sim-badge ${simClass(item.similarity)}`}>{pct}%</span>
              </div>

              {/* Event subtitle */}
              <div className="search-result-event">{item.event}</div>

              {/* Content */}
              <div className="search-result-content">
                {isExpanded && full ? full : excerpt}
                {full && !isExpanded && (
                  <span className="search-read-more" onClick={() => toggleExpand(cardId)} style={{ color: primaryColor }}> ...more</span>
                )}
                {full && isExpanded && (
                  <span className="search-read-less" onClick={() => toggleExpand(cardId)}> show less</span>
                )}
              </div>
            </div>

            {/* YouTube Preview */}
            {ytId && isPreviewOpen && (
              <div className="search-preview-panel open">
                <iframe
                  src={`https://www.youtube.com/embed/${ytId}?start=${startSec}&autoplay=1&rel=0`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`Preview: ${item.speaker}`}
                />
                <div className="search-preview-info">
                  <span>Playing from {formatTime(item.start_time)} · {item.speaker}</span>
                  <a
                    href={`https://www.youtube.com/watch?v=${ytId}&t=${startSec}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in YouTube ↗
                  </a>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Show More */}
      {!loading && !showAll && hiddenCount > 0 && (
        <div className="search-show-more">
          <button onClick={() => setShowAll(true)}>
            Show {hiddenCount} more results
          </button>
        </div>
      )}
    </div>
    </Layout>
  );
};

export default SearchLecturesPage;
